import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES } from './constants'

/**
 * Affiliate Operations routes.
 *
 * Paths come from `docs/v2/route-map.md`, which is the contract between phases — a route exists
 * there before it is built, so two phases cannot invent different paths for the same screen.
 *
 * Every route carries `meta.workspace = 'affiliate'`, so the workspace guard sends a landlord
 * who follows one of these links to their own dashboard rather than a 403. The capability meta
 * is UX: the server refuses regardless (architecture.md §3).
 *
 * Routes for later phases (`/tenants`, `/renewals`, `/commissions`, `/analytics`) are **not**
 * declared. Route-map rule 4: never ship navigation that leads to a blank page.
 *
 * The onboarding flow's affiliate copy (`/landlords/:id/properties/new`, `/properties/:id/edit`)
 * is declared by `modules/onboarding/routes.ts`, next to its landlord twin, so the two cannot
 * drift.
 */
const base = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'affiliate' as const,
}

export const affiliateRoutes: RouteRecordRaw[] = [
  {
    path: '/app/affiliate',
    name: 'affiliate-overview',
    component: () => import('./pages/AffiliateOverviewPage.vue'),
    meta: { ...base, title: 'affiliate.nav.overview' },
  },
  {
    path: '/app/affiliate/landlords',
    name: 'affiliate-landlords',
    component: () => import('./pages/AffiliateLandlordsPage.vue'),
    meta: { ...base, capability: CAPABILITIES.landlordView, title: 'affiliate.nav.landlords' },
  },
  {
    // Phase 06. Before `:id`, so "new" is never read as a landlord id.
    path: '/app/affiliate/landlords/new',
    name: 'affiliate-landlord-new',
    component: () => import('./pages/AffiliateLandlordFormPage.vue'),
    meta: { ...base, capability: CAPABILITIES.landlordCreate, title: 'affiliate.nav.landlords' },
  },
  {
    path: '/app/affiliate/landlords/:id(\\d+)',
    name: 'affiliate-landlord-detail',
    component: () => import('./pages/AffiliateLandlordDetailPage.vue'),
    meta: { ...base, capability: CAPABILITIES.landlordView, title: 'affiliate.nav.landlords' },
  },
  // `/landlords/:id/properties/new` is the onboarding flow — a property is created with its
  // spaces — and is declared by `modules/onboarding/routes.ts`, next to its landlord twin.
  {
    path: '/app/affiliate/properties',
    name: 'affiliate-properties',
    component: () => import('./pages/AffiliatePropertiesPage.vue'),
    meta: { ...base, capability: CAPABILITIES.propertyView, title: 'affiliate.nav.properties' },
  },
  {
    path: '/app/affiliate/properties/:id(\\d+)',
    name: 'affiliate-property-detail',
    component: () => import('./pages/AffiliatePropertyDetailPage.vue'),
    meta: { ...base, capability: CAPABILITIES.propertyView, title: 'affiliate.nav.properties' },
  },
  // `/properties/:id/edit` is the onboarding flow, editing — the permitted property fields and
  // any spaces to add — declared by `modules/onboarding/routes.ts` next to its landlord twin.
  // Students' space requests near the officer's campuses (2026-10-10). Every officer may read
  // it; the server scopes it to their operating states.
  {
    path: '/app/affiliate/student-demand',
    name: 'affiliate-student-demand',
    component: () => import('./pages/AffiliateStudentDemandPage.vue'),
    meta: { ...base, title: 'affiliate.nav.studentDemand' },
  },
  // One request (2026-10-11): the link in the officers' email, and where "Handle this request"
  // is. The server answers 404 outside the officer's states.
  {
    path: '/app/affiliate/student-demand/requests/:reference',
    name: 'affiliate-student-request',
    component: () => import('./pages/AffiliateStudentRequestPage.vue'),
    meta: { ...base, title: 'affiliate.nav.studentDemand' },
  },
  // One roommate request (2026-10-26): handled like a space request -- the link in the officers'
  // roommate digest. The server answers 404 outside the officer's campuses.
  {
    path: '/app/affiliate/student-demand/roommates/:reference',
    name: 'affiliate-student-roommate',
    component: () => import('./pages/AffiliateRoommateRequestPage.vue'),
    meta: { ...base, title: 'affiliate.nav.studentDemand' },
  },
  // Escorted group viewings for the students an officer handles (2026-10-12,
  // requesters-support-whatsapp.md §2). Every officer may read their own; the server refuses a
  // pending or suspended officer's writes, and another officer's viewing is a 404.
  {
    path: '/app/affiliate/viewings',
    name: 'affiliate-viewings',
    component: () => import('./pages/AffiliateViewingsPage.vue'),
    meta: { ...base, title: 'affiliate.nav.viewings' },
  },
  {
    path: '/app/affiliate/viewings/:id',
    name: 'affiliate-viewing',
    component: () => import('./pages/AffiliateViewingPage.vue'),
    meta: { ...base, title: 'affiliate.nav.viewings' },
  },
  // A supervisor's register of the other officers (2026-10-11, requesters-support-whatsapp.md
  // §7). `affiliate.officer.manage` is in the session only while the officer is active and an
  // administrator made them a supervisor; anyone else lands on /403, and the server refuses the
  // API regardless.
  {
    path: '/app/affiliate/officers',
    name: 'affiliate-officers',
    component: () => import('./pages/AffiliateOfficersPage.vue'),
    meta: { ...base, capability: CAPABILITIES.officerManage, title: 'affiliate.nav.officers' },
  },
  {
    // Before `:id`, so "new" is never read as an officer id.
    path: '/app/affiliate/officers/new',
    name: 'affiliate-officer-new',
    component: () => import('./pages/AffiliateOfficerFormPage.vue'),
    meta: { ...base, capability: CAPABILITIES.officerManage, title: 'affiliate.nav.officers' },
  },
  {
    path: '/app/affiliate/officers/:id',
    name: 'affiliate-officer-detail',
    component: () => import('./pages/AffiliateOfficerDetailPage.vue'),
    meta: { ...base, capability: CAPABILITIES.officerManage, title: 'affiliate.nav.officers' },
  },
  {
    path: '/app/affiliate/follow-ups',
    name: 'affiliate-follow-ups',
    component: () => import('./pages/AffiliateFollowUpsPage.vue'),
    meta: { ...base, capability: CAPABILITIES.noteManage, title: 'affiliate.nav.followUps' },
  },
  {
    path: '/app/affiliate/activity',
    name: 'affiliate-activity',
    component: () => import('./pages/AffiliateActivityPage.vue'),
    meta: { ...base, title: 'affiliate.nav.activity' },
  },
  {
    // Deliberately NOT capability-gated. A pending or suspended officer must be able to reach
    // their own code and status — that is the page they need most when the rest of the
    // workspace is refusing them.
    path: '/app/affiliate/profile',
    name: 'affiliate-profile',
    component: () => import('./pages/AffiliateProfilePage.vue'),
    meta: { ...base, title: 'affiliate.nav.profile' },
  },
]
